/**
 * Loading, error and empty states.
 *
 * The rule this file enforces: a failure always says *what* failed and offers a
 * retry. An infinite spinner and a blank page are both indistinguishable from
 * "the fleet is fine", which is the single most dangerous thing this dashboard
 * could imply.
 *
 * `explain()` is the reason this is one file rather than a shrug at each call
 * site: it turns an ApiError into the sentence an operator can act on —
 * unreachable vs unauthenticated vs unauthorised vs a 5xx that belongs in the
 * controller's logs. Those four call for four different next moves.
 */

import type { ReactNode } from 'react';

import { ApiError } from '../lib/api';
import { Callout } from './Callout';
import { Button } from './ui/Button';
import { RenewIcon } from './ui/Icon';

export function LoadingBlock({ label }: { label: string }) {
  return (
    <p className="loading-inline" role="status">
      <span className="loading-inline__spinner" aria-hidden="true" />
      {label}
    </p>
  );
}

/**
 * Placeholder rows while a table loads. Shaped like the content it stands in
 * for, so the page does not reflow into a different layout on arrival.
 */
export function SkeletonRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="card skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="skeleton__line" style={{ width: `${88 - i * 11}%` }} />
      ))}
    </div>
  );
}

/** What went wrong, in the operator's terms, plus how to get out of it. */
function explain(error: unknown): { title: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.isNetwork) {
      return { title: 'Could not reach the controller', detail: error.detail };
    }
    if (error.isUnauthenticated) {
      return {
        title: 'Your session is no longer valid',
        detail: `${error.detail} Sign in again to continue.`,
      };
    }
    if (error.isForbidden) {
      return {
        title: 'Not permitted',
        detail: `${error.detail} — this account is authenticated but is not authorised for ${error.path}.`,
      };
    }
    if (error.status >= 500) {
      return {
        title: `The controller returned ${error.status}`,
        detail: `${error.detail} This is a server-side failure; the controller's logs will have the traceback.`,
      };
    }
    return { title: `Request failed (${error.status})`, detail: error.detail };
  }
  if (error instanceof Error) {
    return { title: 'Something went wrong', detail: error.message };
  }
  return { title: 'Something went wrong', detail: String(error) };
}

export function ErrorBlock({
  what,
  error,
  onRetry,
}: {
  /** The thing that failed, e.g. "the fleet summary". */
  what: string;
  error: unknown;
  onRetry?: () => void;
}) {
  const { title, detail } = explain(error);
  return (
    <Callout tone="critical" title={`${title} while loading ${what}.`} live>
      <p>{detail}</p>
      {onRetry ? (
        <p className="state-block__action">
          <Button kind="tertiary" size="sm" renderIcon={RenewIcon} onClick={onRetry}>
            Try again
          </Button>
        </p>
      ) : null}
    </Callout>
  );
}

/**
 * Shown above data that is still on screen after a refresh failed. The numbers
 * below it are real but old, and saying so is the whole point.
 */
export function StaleBanner({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  const { title, detail } = explain(error);
  return (
    <Callout tone="warning" title={`${title}. Showing the last values that loaded.`} live>
      <p>{detail}</p>
      {onRetry ? (
        <p className="state-block__action">
          <Button kind="ghost" size="sm" renderIcon={RenewIcon} onClick={onRetry}>
            Retry now
          </Button>
        </p>
      ) : null}
    </Callout>
  );
}

export function EmptyBlock({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  /** Optional way out — an empty state that offers nothing is a dead end. */
  action?: ReactNode;
}) {
  return (
    <div className="card empty-block">
      <h3 className="empty-block__title">{title}</h3>
      {children ? <p className="empty-block__detail">{children}</p> : null}
      {action ? <div className="empty-block__action">{action}</div> : null}
    </div>
  );
}

/** The brief's name for it; `EmptyBlock` is kept so existing pages don't move. */
export const EmptyState = EmptyBlock;
